import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { User, PlannedWorkout, NutritionPlan, WorkoutInterval } from '../../types';
import IntervalChart from './IntervalChart';
import ComplianceCard from './ComplianceCard';
import ActualRideChart from './ActualRideChart';
import './AICoachCalendar.css';

interface AICoachCalendarProps {
  user: User;
  refreshTrigger: number;
  onEditGoal?: (goalId: number) => void;
}

interface CalendarActivity {
  id: number;
  name: string;
  type: string;
  start_date: string;
  moving_time: number | null;
  distance: number | null;
  average_watts: number | null;
  average_heartrate: number | null;
  tss: number | null;
  np_watts: number | null;
  if_value: number | null;
  has_stream: boolean;
}

type ViewMode = 'month' | 'week';

const WORKOUT_TYPES = ['endurance', 'tempo', 'intervals', 'recovery', 'race', 'strength', 'rest'];

function getRideId(w: PlannedWorkout): number | null {
  if (w.notes && w.notes.startsWith('ride:')) {
    const id = parseInt(w.notes.split(':')[1]);
    return isNaN(id) ? null : id;
  }
  return null;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h${m.toString().padStart(2, '0')}m` : `${m}m`;
}

function formatDistance(meters: number | null): string {
  if (!meters) return '';
  const km = meters / 1000;
  return km >= 10 ? `${Math.round(km)}km` : `${km.toFixed(1)}km`;
}

function formatSec(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm > 0 ? `${h}h ${rm}min` : `${h}h`;
  }
  return s > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${m}min`;
}

const ZONE_LABEL_COLORS: Record<string, string> = {
  zone1: '#b0b0b0', zone2: '#4a90d9', zone3: '#5cb85c',
  zone4: '#f0ad4e', zone5: '#d9534f', zone6: '#1a1a1a',
};

const ZONE_NAME_MAP: Record<string, string> = {
  recovery: 'zone1', endurance: 'zone2', tempo: 'zone3',
  threshold: 'zone4', lactate_threshold: 'zone4',
  vo2max: 'zone5', vo2_max: 'zone5',
  anaerobic: 'zone6', neuromuscular: 'zone6',
};

function resolveZone(zone: string | undefined): string {
  if (!zone) return 'zone2';
  if (zone.startsWith('zone')) return zone;
  return ZONE_NAME_MAP[zone.toLowerCase()] || 'zone2';
}

function zoneColor(zone: string | undefined): string {
  return ZONE_LABEL_COLORS[resolveZone(zone)] || ZONE_LABEL_COLORS.zone2;
}

function IntervalStructure({ intervals }: { intervals: WorkoutInterval[] }) {
  if (!intervals || intervals.length === 0) return null;

  return (
    <div className="workout-interval-structure">
      {intervals.map((seg, i) => {
        if (seg.type === 'warmup') {
          return (
            <div key={i} className="interval-seg warmup">
              <span className="seg-label" style={{ color: '#b8860b' }}>Warmup</span>
              <span className="seg-time">{formatSec(seg.duration || 0)}</span>
              {seg.power_start_watts && seg.power_end_watts && (
                <span className="seg-power">{seg.power_start_watts}-{seg.power_end_watts}W</span>
              )}
            </div>
          );
        }
        if (seg.type === 'cooldown') {
          return (
            <div key={i} className="interval-seg cooldown">
              <span className="seg-label" style={{ color: '#4682b4' }}>Cooldown</span>
              <span className="seg-time">{formatSec(seg.duration || 0)}</span>
              {seg.power_start_watts && seg.power_end_watts && (
                <span className="seg-power">{seg.power_start_watts}-{seg.power_end_watts}W</span>
              )}
            </div>
          );
        }
        if (seg.type === 'steady') {
          const zone = resolveZone(seg.zone);
          const color = zoneColor(seg.zone);
          return (
            <div key={i} className="interval-seg steady">
              <span className="seg-label" style={{ color }}>{zone.replace('zone', 'Z')} {(seg.zone || '').replace(/^zone\d?/, '').trim()}</span>
              <span className="seg-time">{formatSec(seg.duration || 0)}</span>
              {seg.power_watts && <span className="seg-power">{seg.power_watts}W</span>}
            </div>
          );
        }
        if (seg.type === 'intervals') {
          const onColor = zoneColor(seg.on_zone || seg.zone);
          const offColor = zoneColor(seg.off_zone || 'zone1');
          return (
            <div key={i} className="interval-seg intervals-block">
              <span className="seg-label" style={{ color: onColor }}>{seg.reps}x</span>
              <span className="seg-detail">
                <span style={{ color: onColor, fontWeight: 600 }}>{formatSec(seg.on_duration || 0)} ON</span>
                {seg.on_power_watts && <> @ {seg.on_power_watts}W</>}
                {' / '}
                <span style={{ color: offColor }}>{formatSec(seg.off_duration || 0)} OFF</span>
                {seg.off_power_watts && <> @ {seg.off_power_watts}W</>}
              </span>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

const AICoachCalendar: React.FC<AICoachCalendarProps> = ({ user, refreshTrigger, onEditGoal }) => {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [workouts, setWorkouts] = useState<PlannedWorkout[]>([]);
  const [activities, setActivities] = useState<CalendarActivity[]>([]);
  const [selectedWorkout, setSelectedWorkout] = useState<PlannedWorkout | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [workoutViewMode, setWorkoutViewMode] = useState<'detail' | 'edit'>('detail');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [exportDropdown, setExportDropdown] = useState<{ type: 'workout' | 'week'; workoutId?: number; weekStart?: string } | null>(null);
  const [exportFormat, setExportFormat] = useState<string>('zwo');
  const [exportFtpSource, setExportFtpSource] = useState<string>('indoor');
  const [nutritionPlans, setNutritionPlans] = useState<NutritionPlan[]>([]);
  const [editingNutrition, setEditingNutrition] = useState<NutritionPlan | null>(null);
  const [showNutritionModal, setShowNutritionModal] = useState(false);

  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [dayConflicts, setDayConflicts] = useState<{ goal_id: number; goal_title: string; goal_priority: string }[]>([]);
  const [pushingToIntervals, setPushingToIntervals] = useState(false);
  const [intervalsPushResult, setIntervalsPushResult] = useState<{ pushed: number; skipped: number; failed: number; workouts: { date: string; title: string; type: string; status: string; reason?: string }[] } | null>(null);
  const [intervalsPushError, setIntervalsPushError] = useState<string | null>(null);
  const [gcalConnected, setGcalConnected] = useState(false);

  const handlePushToIntervals = async () => {
    setPushingToIntervals(true);
    setIntervalsPushResult(null);
    setIntervalsPushError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/intervals/push-week`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setIntervalsPushResult(data);
      } else {
        setIntervalsPushError(data.error || 'Push failed');
      }
    } catch {
      setIntervalsPushError('Network error');
    }
    setPushingToIntervals(false);
    setTimeout(() => { setIntervalsPushResult(null); setIntervalsPushError(null); }, 8000);
  };

  const FORMAT_INFO: Record<string, { apps: string; importTip: string }> = {
    fit: { apps: 'Garmin Edge, Garmin Connect, Wahoo', importTip: 'Copy .fit file to your Garmin Edge via USB: Garmin/NewFiles/ folder. Or upload to Garmin Connect > Training > Workouts > Import. It will sync to your Edge automatically.' },
    zwo: { apps: 'Zwift, TrainerRoad, Rouvy', importTip: 'In Zwift: open Zwift > Workouts > Custom Workouts folder, copy the .zwo file there. In TrainerRoad: go to TrainerRoad.com > Calendar > select day > Add Workout > Import.' },
    erg: { apps: 'Zwift, TrainingPeaks, Golden Cheetah', importTip: 'In Zwift: copy to Documents/Zwift/Workouts/[YourID]/ folder. In TrainingPeaks: Calendar > right-click day > Import Workout File.' },
    mrc: { apps: 'Zwift, PerfPRO, Golden Cheetah', importTip: 'In Zwift: copy to Documents/Zwift/Workouts/[YourID]/ folder. In PerfPRO: open File > Import Workout.' },
  };

  const handleExportWorkout = async (workoutId: number, format: string = 'zwo', ftpSource: string = 'indoor') => {
    let res: Response | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        res = await fetch(`${API_BASE_URL}/api/web/coach/export/workout/${workoutId}?format=${format}&ftp_source=${ftpSource}`, {
          credentials: 'include'
        });
        if (res.ok || res.status < 500) break;
      } catch { /* network error, retry */ }
      if (attempt < 2) await new Promise(r => setTimeout(r, 2000));
    }
    if (!res || !res.ok) {
      const err = res ? await res.json().catch(() => ({ error: 'Export failed' })) : { error: 'Network error — try again' };
      alert(err.error || 'Export failed');
      return;
    }
    const ftpUsed = res.headers.get('X-FTP-Used');
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const filename = cd.split('filename=')[1]?.replace(/"/g, '') || `workout.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    setExportSuccess(ftpUsed ? `${format.toUpperCase()} — FTP: ${ftpUsed}` : format);
  };

  const handleExportWeek = async (weekStart: string, format: string = 'zwo', ftpSource: string = 'indoor') => {
    const res = await fetch(`${API_BASE_URL}/api/web/coach/export/week?week_start=${weekStart}&format=${format}&ftp_source=${ftpSource}`, {
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      alert(err.error || 'Export failed');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `alpsepic_week_${weekStart}.zip`; a.click();
    URL.revokeObjectURL(url);
    setExportSuccess(format);
  };

  const openExportDropdown = (type: 'workout' | 'week', e: React.MouseEvent, workoutId?: number, weekStart?: string) => {
    e.stopPropagation();
    setExportFormat('zwo');
    setExportFtpSource('indoor');
    setExportDropdown({ type, workoutId, weekStart });
  };

  const handleSyncToGcal = async (workoutId: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/google-calendar/sync-workout/${workoutId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Sync failed');
    } catch (err) {
      console.error('Failed to sync to Google Calendar:', err);
    }
  };

  const renderExportDropdown = () => {
    if (!exportDropdown) return null;
    const info = FORMAT_INFO[exportFormat];
    return (
      <div className="export-dropdown-overlay" onClick={() => { setExportDropdown(null); setExportSuccess(null); }}>
        <div className="export-dropdown" onClick={(e) => e.stopPropagation()}>
          {exportSuccess ? (
            <>
              <div className="export-dropdown-title">File Downloaded</div>
              <div className="export-import-tip">
                <strong>How to import .{exportSuccess.toUpperCase()} files:</strong>
                <p>{FORMAT_INFO[exportSuccess]?.importTip}</p>
              </div>
              <button className="export-dropdown-download" onClick={() => { setExportDropdown(null); setExportSuccess(null); }}>
                Close
              </button>
            </>
          ) : (
            <>
              <div className="export-dropdown-title">Export {exportDropdown.type === 'week' ? 'Week' : 'Workout'}</div>
              <div className="export-dropdown-field">
                <label>Format</label>
                <div className="export-dropdown-options">
                  {['fit', 'zwo', 'erg', 'mrc'].map(f => (
                    <label key={f} className="export-dropdown-radio">
                      <input type="radio" name="format" value={f} checked={exportFormat === f} onChange={() => setExportFormat(f)} />
                      {f.toUpperCase()}
                    </label>
                  ))}
                </div>
                {info && <div className="export-format-apps">Compatible with: {info.apps}</div>}
              </div>
              <div className="export-dropdown-field">
                <label>FTP Source</label>
                <div className="export-dropdown-options">
                  {['indoor', 'outdoor'].map(s => (
                    <label key={s} className="export-dropdown-radio">
                      <input type="radio" name="ftp" value={s} checked={exportFtpSource === s} onChange={() => setExportFtpSource(s)} />
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <button
                className="export-dropdown-download"
                onClick={() => {
                  if (exportDropdown.type === 'workout' && exportDropdown.workoutId) {
                    handleExportWorkout(exportDropdown.workoutId, exportFormat, exportFtpSource);
                  } else if (exportDropdown.type === 'week' && exportDropdown.weekStart) {
                    handleExportWeek(exportDropdown.weekStart, exportFormat, exportFtpSource);
                  }
                }}
              >
                Download
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const getDateRange = useCallback((): { start: string; end: string } => {
    if (viewMode === 'month') {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth();
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      // Include surrounding days for calendar display
      const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1; // Monday start
      const start = new Date(first);
      start.setDate(start.getDate() - startDay);
      const end = new Date(last);
      const endDay = last.getDay() === 0 ? 0 : 7 - last.getDay();
      end.setDate(end.getDate() + endDay);
      return { start: formatDate(start), end: formatDate(end) };
    } else {
      const day = currentDate.getDay();
      const monday = new Date(currentDate);
      monday.setDate(currentDate.getDate() - (day === 0 ? 6 : day - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: formatDate(monday), end: formatDate(sunday) };
    }
  }, [currentDate, viewMode]);

  const fetchWorkouts = useCallback(async () => {
    const { start, end } = getDateRange();
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/training/workouts?start_date=${start}&end_date=${end}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setWorkouts(Array.isArray(data) ? data : data.workouts || []);
      }
    } catch (e) {
      console.error('Failed to fetch workouts:', e);
    }
  }, [getDateRange]);

  const fetchActivities = useCallback(async () => {
    const { start, end } = getDateRange();
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/calendar/activities?start=${start}&end=${end}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data: CalendarActivity[] = await res.json();
        setActivities(data);
      }
    } catch (e) {
      console.error('Failed to fetch activities:', e);
    }
  }, [getDateRange]);

  const fetchNutrition = useCallback(async () => {
    const { start, end } = getDateRange();
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/nutrition/plans?start_date=${start}&end_date=${end}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setNutritionPlans(data.plans || []);
      }
    } catch (e) {
      console.error('Failed to fetch nutrition plans:', e);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchWorkouts();
    fetchActivities();
    fetchNutrition();
  }, [fetchWorkouts, fetchActivities, fetchNutrition, refreshTrigger]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/web/google-calendar/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.connected && data?.sync_enabled) setGcalConnected(true); })
      .catch(() => {});
  }, []);

  const navigate = (delta: number) => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setMonth(d.getMonth() + delta);
    } else {
      d.setDate(d.getDate() + delta * 7);
    }
    setCurrentDate(d);
    setSelectedDay(null);
  };

  const goToToday = () => { setCurrentDate(new Date()); setSelectedDay(null); };

  const getWorkoutsForDate = (dateStr: string): PlannedWorkout[] => {
    return workouts.filter(w => w.date === dateStr);
  };

  const getActivitiesForDate = (dateStr: string): CalendarActivity[] => {
    return activities.filter(a => a.start_date && a.start_date.startsWith(dateStr));
  };

  const getNutritionForDate = (dateStr: string): NutritionPlan | undefined => {
    return nutritionPlans.find(n => n.date === dateStr);
  };

  const handleNutritionClick = (plan: NutritionPlan, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingNutrition(plan);
    setShowNutritionModal(true);
  };

  const handleNutritionSave = async (data: Partial<NutritionPlan>) => {
    if (!editingNutrition) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/nutrition/plans/${editingNutrition.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) fetchNutrition();
      setShowNutritionModal(false);
    } catch (e) {
      console.error('Failed to update nutrition plan:', e);
    }
  };

  const handleNutritionDelete = async () => {
    if (!editingNutrition) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/nutrition/plans/${editingNutrition.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) fetchNutrition();
      setShowNutritionModal(false);
    } catch (e) {
      console.error('Failed to delete nutrition plan:', e);
    }
  };

  const dayPanelRef = useRef<HTMLDivElement>(null);

  const handleDayClick = (dateStr: string) => {
    const newDay = selectedDay === dateStr ? null : dateStr;
    setSelectedDay(newDay);
    setDayConflicts([]);
    if (newDay) {
      setTimeout(() => dayPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      // Fetch conflicts for this day
      fetch(`${API_BASE_URL}/api/training/goals/conflicts?date=${newDay}`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data?.goal_conflicts) setDayConflicts(data.goal_conflicts); })
        .catch(() => {});
    }
  };

  const handleWorkoutClick = (workout: PlannedWorkout, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedWorkout(workout);
    setIsNew(false);
    setWorkoutViewMode('detail');
    setShowModal(true);
  };

  const handleToggleComplete = async (workout: PlannedWorkout, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/training/workouts/${workout.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed: !workout.completed }),
      });
      if (res.ok) {
        setWorkouts(prev => prev.map(w => w.id === workout.id ? { ...w, completed: !w.completed } : w));
      }
    } catch (e) {
      console.error('Failed to toggle workout:', e);
    }
  };

  const handleSave = async (data: Partial<PlannedWorkout>) => {
    try {
      if (isNew) {
        const res = await fetch(`${API_BASE_URL}/api/web/training/workouts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...data, date: selectedDate || data.date }),
        });
        if (res.ok) {
          fetchWorkouts();
        }
      } else if (selectedWorkout) {
        const res = await fetch(`${API_BASE_URL}/api/web/training/workouts/${selectedWorkout.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        });
        if (res.ok) {
          fetchWorkouts();
        }
      }
      setShowModal(false);
    } catch (e) {
      console.error('Failed to save workout:', e);
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkout) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/training/workouts/${selectedWorkout.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        fetchWorkouts();
        setShowModal(false);
      }
    } catch (e) {
      console.error('Failed to delete workout:', e);
    }
  };

  const handleAddNew = () => {
    setSelectedWorkout(null);
    setSelectedDate(formatDate(new Date()));
    setIsNew(true);
    setShowModal(true);
  };

  const headerText = viewMode === 'month'
    ? currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : (() => {
        const day = currentDate.getDay();
        const monday = new Date(currentDate);
        monday.setDate(currentDate.getDate() - (day === 0 ? 6 : day - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return `${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
      })();

  const dayNames = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

  const renderMonthGrid = () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const start = new Date(first);
    start.setDate(start.getDate() - startDay);

    const days: Date[] = [];
    const d = new Date(start);
    const totalDays = startDay + last.getDate();
    const totalCells = totalDays <= 35 ? 35 : 42;
    for (let i = 0; i < totalCells; i++) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }

    const today = formatDate(new Date());

    return (
      <div className="ai-calendar-grid">
        {dayNames.map(d => (
          <div key={d} className="ai-calendar-day-header">{t(`calendar.days.${d}`)}</div>
        ))}
        {days.map((day, i) => {
          const dateStr = formatDate(day);
          const isOther = day.getMonth() !== m;
          const isToday = dateStr === today;
          const dayWorkouts = getWorkoutsForDate(dateStr);
          const dayActivities = getActivitiesForDate(dateStr);

          const isSelected = dateStr === selectedDay;
          const totalItems = dayWorkouts.length + dayActivities.length;
          const visibleWorkouts = dayWorkouts.slice(0, 3);
          const remainingSlots = 3 - visibleWorkouts.length;
          const visibleActivities = dayActivities.slice(0, Math.max(0, remainingSlots));
          const extraCount = totalItems - visibleWorkouts.length - visibleActivities.length;

          return (
            <div
              key={i}
              className={`ai-calendar-day ${isOther ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => handleDayClick(dateStr)}
            >
              <div className="ai-calendar-day-number">{day.getDate()}</div>
              <div className="ai-calendar-day-workouts">
                {visibleActivities.map((a) => (
                  <div key={`a-${a.id}`} className="ai-calendar-day-label activity-label">
                    <span className="ai-calendar-label-check">&#10003;</span>
                    <span className="ai-calendar-label-text">{a.name}</span>
                  </div>
                ))}
                {visibleWorkouts.map((w) => (
                  w.completed && w.compliance?.actual?.activity_name ? (
                    <div key={w.id} className="ai-calendar-day-label activity-label">
                      <span className="ai-calendar-label-check">&#10003;</span>
                      <span className="ai-calendar-label-text">{w.compliance.actual.activity_name}</span>
                    </div>
                  ) : getRideId(w) ? (
                    <div key={w.id} className="ai-calendar-day-label ride-label">
                      <span className="ai-calendar-label-ride-icon">&#128101;</span>
                      <span className="ai-calendar-label-text">{w.title}</span>
                    </div>
                  ) : (
                    <div
                      key={w.id}
                      className={`ai-calendar-day-label ${w.completed ? 'completed' : ''}`}
                    >
                      <span className={`ai-calendar-label-dot ${w.workout_type}`} />
                      <span className="ai-calendar-label-text">{w.title}</span>
                    </div>
                  )
                ))}
                {getNutritionForDate(dateStr) && (
                  <div className="cal-nutrition-card" title={getNutritionForDate(dateStr)?.ai_notes || ''}>
                    <span className="cal-nutrition-dot" />
                    <span className="ai-calendar-label-text">{getNutritionForDate(dateStr)?.total_kcal} kcal</span>
                  </div>
                )}
                {extraCount > 0 && (
                  <div className="ai-calendar-day-more">+{extraCount}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const day = currentDate.getDay();
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() - (day === 0 ? 6 : day - 1));
    const today = formatDate(new Date());

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push(d);
    }

    return (
      <div className="ai-calendar-week">
        {days.map((d, i) => {
          const dateStr = formatDate(d);
          const isToday = dateStr === today;
          const dayWorkouts = getWorkoutsForDate(dateStr);
          const dayActivities = getActivitiesForDate(dateStr);

          return (
            <div key={i} className={`ai-calendar-week-day ${isToday ? 'today' : ''}`} onClick={() => handleDayClick(dateStr)}>
              <div className="ai-calendar-week-day-label">
                {t(`calendar.days.${dayNames[i]}`)}
                <div className="ai-calendar-week-day-date">{d.getDate()}</div>
              </div>
              <div className="ai-calendar-week-workouts">
                {dayWorkouts.length === 0 && dayActivities.length === 0 && <div className="ai-calendar-empty">&nbsp;</div>}
                {dayActivities.map(a => (
                  <div key={`a-${a.id}`} className="ai-calendar-activity-card">
                    <div className="ai-calendar-activity-check">&#10003;</div>
                    <div className="ai-calendar-workout-info">
                      <div className="title">{a.name}</div>
                      <div className="meta">
                        {a.type}
                        {a.moving_time ? ` | ${formatDuration(a.moving_time)}` : ''}
                        {a.distance ? ` | ${formatDistance(a.distance)}` : ''}
                        {a.tss ? ` | TSS ${Math.round(a.tss)}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                {dayWorkouts.map(w => {
                  const actual = w.completed && w.compliance?.actual ? w.compliance.actual : null;
                  const rideId = getRideId(w);
                  return (
                  <div
                    key={w.id}
                    className={`ai-calendar-workout-card ${w.completed ? 'completed' : ''} ${w.goal_id ? 'goal-linked' : ''} ${rideId ? 'ride-linked' : ''}`}
                    onClick={(e) => {
                      if (rideId) {
                        e.stopPropagation();
                        window.location.href = `/rides/${rideId}`;
                      } else if (w.goal_id && onEditGoal) {
                        e.stopPropagation();
                        onEditGoal(w.goal_id);
                      } else {
                        handleWorkoutClick(w, e);
                      }
                    }}
                  >
                    {actual ? (
                      <div className="ai-calendar-activity-check" style={{ width: 16, height: 16, fontSize: 9 }}>&#10003;</div>
                    ) : rideId ? (
                      <svg className="ai-calendar-ride-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    ) : (
                      <div className={`ai-calendar-workout-type-dot ${w.workout_type}`} />
                    )}
                    <div className="ai-calendar-workout-info">
                      <div className="title">{actual?.activity_name || w.title}</div>
                      {actual ? (
                        <div className="meta">
                          {actual.duration_s ? formatDuration(actual.duration_s) : ''}
                          {actual.distance_km ? ` | ${actual.distance_km} km` : ''}
                          {actual.avg_power ? ` | ${Math.round(actual.avg_power)}W` : ''}
                          {actual.tss ? ` | TSS ${Math.round(actual.tss)}` : ''}
                        </div>
                      ) : (
                        <div className="meta">
                          {w.duration_minutes && `${w.duration_minutes} min`}
                          {w.distance_km && ` | ${w.distance_km} km`}
                          {w.target_zone && ` | ${w.target_zone}`}
                          {w.planned_np && ` | NP ${w.planned_np}W`}
                          {w.planned_avg_hr && ` | HR ${w.planned_avg_hr}`}
                        </div>
                      )}
                    </div>
                    {!actual && (
                    <button
                      className="ai-calendar-export-icon"
                      onClick={(e) => openExportDropdown('workout', e, w.id)}
                      title="Export workout"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                    )}
                    <div
                      className={`ai-calendar-workout-check ${w.completed ? 'checked' : ''}`}
                      onClick={(e) => handleToggleComplete(w, e)}
                    >
                      {w.completed && '✓'}
                    </div>
                  </div>
                  );
                })}
                {(() => {
                  const dayNutrition = getNutritionForDate(dateStr);
                  return dayNutrition ? (
                    <div className="cal-nutrition-card" title={dayNutrition.ai_notes || ''} onClick={(e) => handleNutritionClick(dayNutrition, e)}>
                      <span className="cal-nutrition-dot" />
                      {dayNutrition.total_kcal} kcal
                      <span className="cal-nutrition-macros">
                        P {dayNutrition.protein_g}g · C {dayNutrition.carbs_g}g · F {dayNutrition.fat_g}g
                      </span>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayPanel = () => {
    if (!selectedDay || viewMode !== 'month') return null;
    const dayWorkouts = getWorkoutsForDate(selectedDay);
    const dayActivities = getActivitiesForDate(selectedDay);
    const dateObj = new Date(selectedDay + 'T12:00:00');
    const dateLabel = dateObj.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

    return (
      <div className="ai-calendar-day-panel" ref={dayPanelRef}>
        <div className="ai-calendar-day-panel-header">
          <span className="ai-calendar-day-panel-title">{dateLabel}</span>
          <div className="ai-calendar-day-panel-actions">
            <button
              className="ai-calendar-day-panel-add"
              onClick={() => {
                setSelectedDate(selectedDay);
                setSelectedWorkout(null);
                setIsNew(true);
                setShowModal(true);
              }}
            >
              + {t('calendar.addWorkout')}
            </button>
            <button className="ai-calendar-day-panel-close" onClick={() => setSelectedDay(null)}>×</button>
          </div>
        </div>
        {dayConflicts.length > 0 && (
          <div className="ai-calendar-conflict-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div className="ai-calendar-conflict-text">
              <strong>Scheduling conflict</strong>
              {dayConflicts.map(c => (
                <span key={c.goal_id} className="ai-calendar-conflict-goal">
                  {c.goal_title} ({c.goal_priority})
                </span>
              ))}
            </div>
          </div>
        )}
        {dayWorkouts.length === 0 && dayActivities.length === 0 ? (
          <div className="ai-calendar-day-panel-empty">{t('calendar.noWorkouts')}</div>
        ) : (
          <div className="ai-calendar-day-panel-list">
            {dayActivities.map(a => (
              <div key={`a-${a.id}`} className="ai-calendar-activity-card">
                <div className="ai-calendar-activity-check">&#10003;</div>
                <div className="ai-calendar-workout-info">
                  <div className="title">{a.name}</div>
                  <div className="meta">
                    {a.type}
                    {a.moving_time ? ` | ${formatDuration(a.moving_time)}` : ''}
                    {a.distance ? ` | ${formatDistance(a.distance)}` : ''}
                    {a.tss ? ` | TSS ${Math.round(a.tss)}` : ''}
                    {a.average_watts ? ` | ${Math.round(a.average_watts)}W` : ''}
                    {!a.average_watts && a.average_heartrate ? ` | ${Math.round(a.average_heartrate)} bpm` : ''}
                  </div>
                  {(a.average_watts || a.average_heartrate) && (
                    <ActualRideChart activityId={a.id} height={50} />
                  )}
                </div>
              </div>
            ))}
            {dayWorkouts.map(w => {
              const rideId = getRideId(w);
              return (
              <div key={w.id} className={`ai-calendar-workout-card ${w.completed ? 'completed' : ''} ${rideId ? 'ride-linked' : ''}`}>
                <div
                  className={`ai-calendar-workout-check ${w.completed ? 'checked' : ''}`}
                  onClick={(e) => handleToggleComplete(w, e)}
                >
                  {w.completed && '✓'}
                </div>
                {rideId ? (
                  <svg className="ai-calendar-ride-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                ) : (
                  <div className={`ai-calendar-workout-type-dot ${w.workout_type}`} />
                )}
                <div className="ai-calendar-workout-info">
                  <div className="title">{w.title}</div>
                  <div className="meta">
                    {t(`calendar.workoutTypes.${w.workout_type}`)}
                    {w.duration_minutes && ` · ${w.duration_minutes} min`}
                    {w.distance_km && ` · ${w.distance_km} km`}
                    {w.target_zone && ` · ${w.target_zone}`}
                    {w.target_tss && ` · TSS ${w.target_tss}`}
                    {w.planned_np && ` · NP ${w.planned_np}W`}
                    {w.planned_avg_hr && ` · HR ${w.planned_avg_hr}`}
                  </div>
                  {w.completed && w.compliance?.actual?.activity_id ? (
                    <>
                      <ActualRideChart activityId={w.compliance.actual.activity_id} height={50} />
                      <ComplianceCard compliance={w.compliance} />
                    </>
                  ) : (
                    <>
                      {w.intervals && w.intervals.length > 0 && (
                        <IntervalStructure intervals={w.intervals} />
                      )}
                      {w.intervals && w.intervals.length > 0 && (
                        <IntervalChart intervals={w.intervals} height={50} showLabels />
                      )}
                      {w.compliance && (
                        <ComplianceCard compliance={w.compliance} />
                      )}
                    </>
                  )}
                </div>
                <button
                  className="ai-calendar-export-icon"
                  onClick={(e) => openExportDropdown('workout', e, w.id)}
                  title="Export workout"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                {gcalConnected && !w.completed && (
                  <button
                    className="ai-calendar-export-icon"
                    onClick={(e) => { e.stopPropagation(); handleSyncToGcal(w.id); }}
                    title="Sync to Google Calendar"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </button>
                )}
                {rideId ? (
                  <button
                    className="ai-calendar-day-panel-edit goal-edit"
                    onClick={(e) => { e.stopPropagation(); window.location.href = `/rides/${rideId}`; }}
                  >
                    View Ride
                  </button>
                ) : w.goal_id && onEditGoal ? (
                  <button
                    className="ai-calendar-day-panel-edit goal-edit"
                    onClick={(e) => { e.stopPropagation(); onEditGoal(w.goal_id!); }}
                  >
                    Edit Goal
                  </button>
                ) : (
                  <button
                    className="ai-calendar-day-panel-edit"
                    onClick={(e) => { e.stopPropagation(); handleWorkoutClick(w, e); }}
                  >
                    {t('calendar.form.editWorkout')}
                  </button>
                )}
                <button
                  className="ai-calendar-day-panel-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    (async () => {
                      try {
                        const res = await fetch(`${API_BASE_URL}/api/web/training/workouts/${w.id}`, {
                          method: 'DELETE',
                          credentials: 'include',
                        });
                        if (res.ok) fetchWorkouts();
                      } catch (err) {
                        console.error('Failed to delete workout:', err);
                      }
                    })();
                  }}
                >
                  {t('calendar.form.delete')}
                </button>
              </div>
              );
            })}
            {(() => {
              const dayNutrition = getNutritionForDate(selectedDay);
              return dayNutrition ? (
                <div className="cal-nutrition-card cal-nutrition-card-panel" onClick={(e) => handleNutritionClick(dayNutrition, e)}>
                  <span className="cal-nutrition-dot" />
                  <div className="cal-nutrition-details">
                    <div>{dayNutrition.total_kcal} kcal</div>
                    <div className="cal-nutrition-macros">
                      P {dayNutrition.protein_g}g · C {dayNutrition.carbs_g}g · F {dayNutrition.fat_g}g
                    </div>
                    {dayNutrition.ai_notes && <div className="cal-nutrition-notes">{dayNutrition.ai_notes}</div>}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ai-calendar">
      <div className="ai-calendar-header">
        <div className="ai-calendar-nav">
          <button onClick={() => navigate(-1)}>&#8249;</button>
          <span className="ai-calendar-title">{headerText}</span>
          <button onClick={() => navigate(1)}>&#8250;</button>
          <button onClick={goToToday}>{t('calendar.today')}</button>
        </div>
        <div className="ai-calendar-view-toggle">
          <button className={viewMode === 'month' ? 'active' : ''} onClick={() => { setViewMode('month'); setSelectedDay(null); }}>
            {t('calendar.month')}
          </button>
          <button className={viewMode === 'week' ? 'active' : ''} onClick={() => { setViewMode('week'); setSelectedDay(null); }}>
            {t('calendar.week')}
          </button>
        </div>
        <div className="ai-calendar-actions">
          <button
            className="ai-calendar-export-week-btn"
            onClick={(e) => {
              const day = currentDate.getDay();
              const monday = new Date(currentDate);
              monday.setDate(currentDate.getDate() - (day === 0 ? 6 : day - 1));
              openExportDropdown('week', e, undefined, formatDate(monday));
            }}
          >
            Export Week
          </button>
          {user?.intervals_connected ? (
            <button
              className="ai-calendar-export-week-btn"
              style={{ backgroundColor: '#4A90D9', color: '#fff', border: 'none' }}
              onClick={handlePushToIntervals}
              disabled={pushingToIntervals}
            >
              {pushingToIntervals ? 'Exporting...' : '↗ Export'}
            </button>
          ) : (
            <button
              className="ai-calendar-export-week-btn"
              style={{ backgroundColor: '#888', color: '#fff', border: 'none', opacity: 0.7 }}
              onClick={() => window.location.href = `${API_BASE_URL}/auth/intervals/start`}
              title="Connect Intervals.icu to export"
            >
              ↗ Export
            </button>
          )}
          <button className="ai-calendar-add-btn" onClick={handleAddNew}>
            + {t('calendar.addWorkout')}
          </button>
        </div>
        {intervalsPushError && (
          <div style={{ fontSize: '12px', color: '#d9534f', padding: '4px 12px' }}>
            {intervalsPushError}
          </div>
        )}
        {intervalsPushResult && (
          <div style={{ fontSize: '12px', padding: '8px 12px', background: 'rgba(255,255,255,0.9)', borderRadius: 8, margin: '4px 12px', border: '1px solid #e0e0e0' }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>
              Pushed {intervalsPushResult.pushed} workout{intervalsPushResult.pushed !== 1 ? 's' : ''}
              {intervalsPushResult.skipped > 0 && <>, skipped {intervalsPushResult.skipped}</>}
              {intervalsPushResult.failed > 0 && <span style={{ color: '#d9534f' }}>, failed {intervalsPushResult.failed}</span>}
            </div>
            {intervalsPushResult.workouts && intervalsPushResult.workouts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {intervalsPushResult.workouts.map((w, i) => {
                  const d = new Date(w.date + 'T12:00:00');
                  const dayLabel = d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: '2-digit' });
                  const icon = w.status === 'pushed' ? '\u2713' : w.status === 'skipped' ? '\u2298' : '\u2717';
                  const color = w.status === 'pushed' ? '#5cb85c' : w.status === 'skipped' ? '#999' : '#d9534f';
                  return (
                    <div key={i} style={{ color, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ width: 14, textAlign: 'center' }}>{icon}</span>
                      <span style={{ color: '#666', minWidth: 70 }}>{dayLabel}</span>
                      <span style={{ color: w.status === 'pushed' ? '#333' : color }}>
                        {w.title}
                        {w.status === 'skipped' && w.reason && <span style={{ color: '#999' }}> ({w.reason})</span>}
                        {w.status === 'failed' && <span> (failed)</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {viewMode === 'month' ? renderMonthGrid() : renderWeekView()}
      {renderDayPanel()}

      {renderExportDropdown()}

      {showModal && selectedWorkout && !isNew && workoutViewMode === 'detail' && (
        <WorkoutDetailView
          workout={selectedWorkout}
          nutrition={getNutritionForDate(selectedWorkout.date)}
          onEdit={() => setWorkoutViewMode('edit')}
          onEditNutrition={() => {
            const nutr = getNutritionForDate(selectedWorkout.date);
            if (nutr) {
              setEditingNutrition(nutr);
              setShowModal(false);
              setWorkoutViewMode('detail');
              setShowNutritionModal(true);
            }
          }}
          onClose={() => { setShowModal(false); setWorkoutViewMode('detail'); }}
        />
      )}

      {showModal && (isNew || workoutViewMode === 'edit') && (
        <WorkoutModal
          workout={selectedWorkout}
          date={selectedDate}
          isNew={isNew}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => { setShowModal(false); setWorkoutViewMode('detail'); }}
        />
      )}

      {showNutritionModal && editingNutrition && (
        <NutritionModal
          plan={editingNutrition}
          onSave={handleNutritionSave}
          onDelete={handleNutritionDelete}
          onClose={() => { setShowNutritionModal(false); }}
        />
      )}
    </div>
  );
};

interface NutritionModalProps {
  plan: NutritionPlan;
  onSave: (data: Partial<NutritionPlan>) => void;
  onDelete: () => void;
  onClose: () => void;
}

const NutritionModal: React.FC<NutritionModalProps> = ({ plan, onSave, onDelete, onClose }) => {
  const [kcal, setKcal] = useState(plan.total_kcal || 0);
  const [protein, setProtein] = useState(plan.protein_g || 0);
  const [carbs, setCarbs] = useState(plan.carbs_g || 0);
  const [fat, setFat] = useState(plan.fat_g || 0);

  return (
    <div className="ai-calendar-modal-overlay" onClick={onClose}>
      <div className="ai-calendar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-calendar-modal-header">
          <h3>Nutrition Plan — {plan.date}</h3>
          <button className="ai-calendar-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ai-calendar-modal-body">
          <div className="ai-calendar-form-row">
            <label>Total kcal</label>
            <input type="number" value={kcal} onChange={(e) => setKcal(parseInt(e.target.value) || 0)} />
          </div>
          <div className="ai-calendar-form-row">
            <label>Protein (g)</label>
            <input type="number" value={protein} onChange={(e) => setProtein(parseInt(e.target.value) || 0)} />
          </div>
          <div className="ai-calendar-form-row">
            <label>Carbs (g)</label>
            <input type="number" value={carbs} onChange={(e) => setCarbs(parseInt(e.target.value) || 0)} />
          </div>
          <div className="ai-calendar-form-row">
            <label>Fat (g)</label>
            <input type="number" value={fat} onChange={(e) => setFat(parseInt(e.target.value) || 0)} />
          </div>
          {plan.ai_notes && (
            <div className="cal-nutrition-notes-display">
              <label>AI Notes</label>
              <p>{plan.ai_notes}</p>
            </div>
          )}
        </div>
        <div className="ai-calendar-modal-footer">
          <button className="ai-calendar-btn-delete" onClick={onDelete}>Delete</button>
          <button className="ai-calendar-btn-save" onClick={() => onSave({ total_kcal: kcal, protein_g: protein, carbs_g: carbs, fat_g: fat })}>Save</button>
        </div>
      </div>
    </div>
  );
};

interface WorkoutDetailViewProps {
  workout: PlannedWorkout;
  nutrition?: NutritionPlan;
  onEdit: () => void;
  onEditNutrition?: () => void;
  onClose: () => void;
}

const WORKOUT_TYPE_LABELS: Record<string, string> = {
  endurance: 'Endurance', tempo: 'Tempo', intervals: 'Intervals',
  recovery: 'Recovery', race: 'Race', strength: 'Strength', rest: 'Rest',
};

const WorkoutDetailView: React.FC<WorkoutDetailViewProps> = ({ workout, nutrition, onEdit, onEditNutrition, onClose }) => {
  const hasIntervals = workout.intervals && workout.intervals.length > 0;

  const hasPre = nutrition && nutrition.pre_kcal != null && nutrition.pre_kcal > 0;
  const hasDuring = nutrition && nutrition.during_carbs_g != null && nutrition.during_carbs_g > 0;
  const hasPost = nutrition && nutrition.post_kcal != null && nutrition.post_kcal > 0;
  const hasNutrition = nutrition && (nutrition.total_kcal || hasPre || hasDuring || hasPost);

  const formatNutritionTiming = (minutes: number | null | undefined) => {
    if (!minutes) return '';
    if (minutes >= 60) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? `${h}h ${m}min` : `${h}h`;
    }
    return `${minutes} min`;
  };

  return (
    <div className="ai-calendar-modal-overlay" onClick={onClose}>
      <div className="ai-calendar-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="ai-calendar-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>{workout.title}</h3>
            <span
              className="cal-workout-type-badge"
              style={{
                background: zoneColor(workout.target_zone),
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {WORKOUT_TYPE_LABELS[workout.workout_type] || workout.workout_type}
            </span>
          </div>
          <button className="ai-calendar-modal-close" onClick={onClose}>x</button>
        </div>

        <div className="ai-calendar-modal-body" style={{ padding: '16px 20px' }}>
          <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>{workout.date}</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', marginBottom: 16 }}>
            {workout.duration_minutes != null && workout.duration_minutes > 0 && (
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#888' }}>Duration: </span>
                <strong>{formatDuration(workout.duration_minutes * 60)}</strong>
              </div>
            )}
            {workout.distance_km != null && workout.distance_km > 0 && (
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#888' }}>Distance: </span>
                <strong>{workout.distance_km} km</strong>
              </div>
            )}
            {workout.target_zone && (
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#888' }}>Zone: </span>
                <strong style={{ color: zoneColor(workout.target_zone) }}>
                  {resolveZone(workout.target_zone).replace('zone', 'Z')}
                </strong>
              </div>
            )}
            {workout.target_tss != null && workout.target_tss > 0 && (
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#888' }}>TSS: </span>
                <strong>{workout.target_tss}</strong>
              </div>
            )}
          </div>

          {hasIntervals && (
            <div style={{ marginBottom: 16 }}>
              <IntervalChart intervals={workout.intervals!} height={120} showLabels />
            </div>
          )}

          {workout.description && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</div>
              <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', color: '#333' }}>
                {workout.description}
              </p>
            </div>
          )}

          {workout.notes && !workout.notes.startsWith('ride:') && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</div>
              <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap', color: '#555' }}>
                {workout.notes}
              </p>
            </div>
          )}

          {/* Nutrition section */}
          {hasNutrition && (
            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666' }}>
                  Nutrition
                </div>
                {onEditNutrition && (
                  <button
                    onClick={onEditNutrition}
                    style={{
                      background: 'none', border: '1px solid #ccc', borderRadius: 4,
                      padding: '2px 8px', fontSize: 11, color: '#666', cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Daily totals */}
              <div style={{
                background: '#f8f8f8', borderRadius: 8, padding: 12, marginBottom: 10,
                border: '1px solid #eee',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginBottom: 4 }}>
                  {nutrition!.total_kcal || 0} <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>kcal</span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                  <div><span style={{ color: '#888' }}>P: </span><strong>{nutrition!.protein_g || 0}g</strong></div>
                  <div><span style={{ color: '#888' }}>C: </span><strong>{nutrition!.carbs_g || 0}g</strong></div>
                  <div><span style={{ color: '#888' }}>F: </span><strong>{nutrition!.fat_g || 0}g</strong></div>
                </div>
              </div>

              {/* Pre-workout */}
              {hasPre && (
                <div style={{
                  background: '#fafafa', borderRadius: 6, padding: 10, marginBottom: 8,
                  borderLeft: '3px solid #b8b8b8',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', marginBottom: 4 }}>
                    Przed treningiem
                  </div>
                  {nutrition!.pre_timing_minutes != null && nutrition!.pre_timing_minutes > 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                      {formatNutritionTiming(nutrition!.pre_timing_minutes)} przed treningiem
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                    <div><span style={{ color: '#888' }}>kcal: </span><strong>{nutrition!.pre_kcal}</strong></div>
                    {nutrition!.pre_protein_g != null && <div><span style={{ color: '#888' }}>P: </span><strong>{nutrition!.pre_protein_g}g</strong></div>}
                    {nutrition!.pre_carbs_g != null && <div><span style={{ color: '#888' }}>C: </span><strong>{nutrition!.pre_carbs_g}g</strong></div>}
                    {nutrition!.pre_fat_g != null && <div><span style={{ color: '#888' }}>F: </span><strong>{nutrition!.pre_fat_g}g</strong></div>}
                  </div>
                </div>
              )}

              {/* During workout */}
              {hasDuring && (
                <div style={{
                  background: '#fafafa', borderRadius: 6, padding: 10, marginBottom: 8,
                  borderLeft: '3px solid #b8b8b8',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', marginBottom: 4 }}>
                    W trakcie treningu
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                    {nutrition!.during_carbs_g != null && <div><span style={{ color: '#888' }}>Carbs: </span><strong>{nutrition!.during_carbs_g}g</strong></div>}
                    {nutrition!.during_sodium_mg != null && <div><span style={{ color: '#888' }}>Sodium: </span><strong>{nutrition!.during_sodium_mg}mg</strong></div>}
                    {nutrition!.during_fluid_ml != null && <div><span style={{ color: '#888' }}>Fluid: </span><strong>{nutrition!.during_fluid_ml}ml</strong></div>}
                  </div>
                </div>
              )}

              {/* Post-workout */}
              {hasPost && (
                <div style={{
                  background: '#fafafa', borderRadius: 6, padding: 10, marginBottom: 8,
                  borderLeft: '3px solid #b8b8b8',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', marginBottom: 4 }}>
                    Po treningu
                  </div>
                  {nutrition!.post_timing_minutes != null && nutrition!.post_timing_minutes > 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                      {formatNutritionTiming(nutrition!.post_timing_minutes)} po treningu
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                    <div><span style={{ color: '#888' }}>kcal: </span><strong>{nutrition!.post_kcal}</strong></div>
                    {nutrition!.post_protein_g != null && <div><span style={{ color: '#888' }}>P: </span><strong>{nutrition!.post_protein_g}g</strong></div>}
                    {nutrition!.post_carbs_g != null && <div><span style={{ color: '#888' }}>C: </span><strong>{nutrition!.post_carbs_g}g</strong></div>}
                    {nutrition!.post_fat_g != null && <div><span style={{ color: '#888' }}>F: </span><strong>{nutrition!.post_fat_g}g</strong></div>}
                  </div>
                </div>
              )}

              {/* AI Notes */}
              {nutrition!.ai_notes && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Notes</div>
                  <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap', color: '#555' }}>
                    {nutrition!.ai_notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ai-calendar-modal-footer">
          <button className="ai-calendar-btn-save" onClick={onEdit} style={{ background: '#555' }}>Edit</button>
          <button className="ai-calendar-btn-save" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

interface WorkoutModalProps {
  workout: PlannedWorkout | null;
  date: string | null;
  isNew: boolean;
  onSave: (data: Partial<PlannedWorkout>) => void;
  onDelete: () => void;
  onClose: () => void;
}

const WorkoutModal: React.FC<WorkoutModalProps> = ({ workout, date, isNew, onSave, onDelete, onClose }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState(workout?.title || '');
  const [workoutDate, setWorkoutDate] = useState(workout?.date || date || formatDate(new Date()));
  const [workoutType, setWorkoutType] = useState(workout?.workout_type || 'endurance');
  const [duration, setDuration] = useState(workout?.duration_minutes?.toString() || '');
  const [distance, setDistance] = useState(workout?.distance_km?.toString() || '');
  const [targetZone, setTargetZone] = useState(workout?.target_zone || '');
  const [description, setDescription] = useState(workout?.description || '');
  const [notes, setNotes] = useState(workout?.notes || '');

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      date: workoutDate,
      workout_type: workoutType,
      duration_minutes: duration ? parseInt(duration) : undefined,
      distance_km: distance ? parseFloat(distance) : undefined,
      target_zone: targetZone || undefined,
      description: description || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <div className="workout-modal-overlay" onClick={onClose}>
      <div className="workout-modal" onClick={e => e.stopPropagation()}>
        <h3>{isNew ? t('calendar.form.newWorkout') : t('calendar.form.editWorkout')}</h3>

        <div className="workout-modal-field">
          <label>{t('calendar.form.title')}</label>
          <input value={title} onChange={e => setTitle(e.target.value)} autoFocus />
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.date')}</label>
          <input type="date" value={workoutDate} onChange={e => setWorkoutDate(e.target.value)} />
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.type')}</label>
          <select value={workoutType} onChange={e => setWorkoutType(e.target.value)}>
            {WORKOUT_TYPES.map(wt => (
              <option key={wt} value={wt}>{t(`calendar.workoutTypes.${wt}`)}</option>
            ))}
          </select>
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.duration')}</label>
          <input type="number" value={duration} onChange={e => setDuration(e.target.value)} min="0" />
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.distance')}</label>
          <input type="number" value={distance} onChange={e => setDistance(e.target.value)} min="0" step="0.1" />
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.zone')}</label>
          <select value={targetZone} onChange={e => setTargetZone(e.target.value)}>
            <option value="">-</option>
            <option value="z1">{t('calendar.zones.z1')}</option>
            <option value="z2">{t('calendar.zones.z2')}</option>
            <option value="z3">{t('calendar.zones.z3')}</option>
            <option value="z4">{t('calendar.zones.z4')}</option>
            <option value="z5">{t('calendar.zones.z5')}</option>
          </select>
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.description')}</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
        </div>

        <div className="workout-modal-field">
          <label>{t('calendar.form.notes')}</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
        </div>

        {workout?.intervals && workout.intervals.length > 0 && (
          <div className="workout-modal-field">
            <label>Interval Structure</label>
            <IntervalChart intervals={workout.intervals} height={60} />
          </div>
        )}

        {workout?.compliance && (
          <ComplianceCard compliance={workout.compliance} />
        )}

        <div className="workout-modal-actions">
          {!isNew && (
            <button className="workout-modal-delete" onClick={onDelete}>{t('calendar.form.delete')}</button>
          )}
          <button className="workout-modal-cancel" onClick={onClose}>{t('calendar.form.cancel')}</button>
          <button className="workout-modal-save" onClick={handleSave}>{t('calendar.form.save')}</button>
        </div>
      </div>
    </div>
  );
};

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default AICoachCalendar;
